import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  SlideFade,
  Icon,
  VStack,
  Container,
  Spinner,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion, AnimatePresence } from 'framer-motion';
import ExcelGridDataEntry from './DownloadFormat';
import { ForbiddenPage } from '../Forbidden/Forbidden';
import { FiDatabase, FiTrendingUp, FiShield } from 'react-icons/fi';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulseAnimation = keyframes`
  0% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
  100% { opacity: 0.4; transform: scale(1); }
`;

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const UploadSection = () => {
  // ✅ ALL HOOKS MUST BE CALLED AT THE TOP LEVEL - BEFORE ANY CONDITIONAL RETURNS
  const bgGradient = useColorModeValue(
    'linear-gradient(135deg, #f6f9fc 0%, #eef2f7 100%)',
    'linear-gradient(135deg, #0f172a 0%, #1a1f3a 100%)',
  );

  const headerBg = useColorModeValue(
    'rgba(255, 255, 255, 0.9)',
    'rgba(15, 23, 42, 0.9)',
  );
  
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('#2c7a7a', '#4fd1c5');
  
  const loaderBg = useColorModeValue('gray.50', 'gray.900');
  const loaderPattern = useColorModeValue(
    'radial-gradient(circle at 25px 25px, rgba(44, 122, 122, 0.1) 2%, transparent 0%)',
    'radial-gradient(circle at 25px 25px, rgba(79, 209, 197, 0.1) 2%, transparent 0%)',
  );

  // Additional color mode values for the main content (used in JSX)
  const mainBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const loadUser = () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        setUser(userData);
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
        setTimeout(() => setShowContent(true), 300);
      }
    };

    setTimeout(loadUser, 100);
  }, []);

  // Loading state - after all hooks
  if (isLoading) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={loaderBg}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundImage={loaderPattern}
          backgroundSize="100px 100px"
        />
        
        <VStack spacing={6} zIndex={1}>
          <Box
            width="80px"
            height="80px"
            borderRadius="full"
            bgGradient="linear(135deg, #2c7a7a, #4fd1c5)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            animation={`${pulseAnimation} 2s ease-in-out infinite`}
          >
            <Icon as={FiDatabase} boxSize={10} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="medium" color={accentColor}>
            Loading Data Entry Portal...
          </Text>
          <Spinner size="lg" color={accentColor} thickness="3px" />
        </VStack>
      </Flex>
    );
  }

  // Forbidden check - after all hooks
  if (
    !user ||
    (user.role !== 'talukaUser' &&
      user.role !== 'schemeAdmin' &&
      user.role !== 'subDivisionalOfficer' &&
      user.role !== 'projectOfficer')
  ) {
    return <ForbiddenPage />;
  }

  // Main return
  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Animated Background Patterns */}
      <Box
        position="absolute"
        top="-50%"
        left="-50%"
        width="200%"
        height="200%"
        background="radial-gradient(circle, rgba(44, 122, 122, 0.08) 1px, transparent 1px)"
        backgroundSize="60px 60px"
        opacity="0.5"
        pointerEvents="none"
      />
      
      <Box
        position="absolute"
        top="20%"
        right="-10%"
        width="500px"
        height="500px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(79, 209, 197, 0.1), transparent)"
        filter="blur(60px)"
        pointerEvents="none"
      />
      
      <Box
        position="absolute"
        bottom="10%"
        left="-5%"
        width="400px"
        height="400px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(44, 122, 122, 0.1), transparent)"
        filter="blur(50px)"
        pointerEvents="none"
      />

      {/* Main Content */}
      <Container maxW="100%" px={{ base: 1, md: 1, lg: 1 }} py={{ base: 4, md: 1 }}>
        <AnimatePresence mode="wait">
          <SlideFade in={showContent} offsetY="20px">
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >

              {/* Main Grid Component - Full Width */}
              <MotionBox
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                layout
              >
                <Box
                  bg={mainBg}
                  borderRadius="2xl"
                  boxShadow="0 20px 60px rgba(0,0,0,0.12)"
                  position="relative"
                  overflow="hidden"
                  _before={{
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    bgGradient: 'linear(90deg, #2c7a7a, #4fd1c5, #2c7a7a)',
                    backgroundSize: '200% 200%',
                    animation: `${gradientAnimation} 3s ease infinite`,
                  }}
                >
                  <Box p={{ base: 1, md: 2 }}>
                    <ExcelGridDataEntry />
                  </Box>
                </Box>
              </MotionBox>

              {/* Footer Stats */}
              {/* <MotionFlex
                justify="center"
                mt={8}
                gap={6}
                wrap="wrap"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Flex 
                  align="center" 
                  gap={2}
                  bg={headerBg}
                  backdropFilter="blur(10px)"
                  px={4}
                  py={2}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Box w="8px" h="8px" borderRadius="full" bg="#2c7a7a" />
                  <Text fontSize="xs" color={subtitleColor}>
                    Secure Encrypted Connection
                  </Text>
                </Flex>
                
                <Flex 
                  align="center" 
                  gap={2}
                  bg={headerBg}
                  backdropFilter="blur(10px)"
                  px={4}
                  py={2}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Icon as={FiTrendingUp} boxSize={3} color="#2c7a7a" />
                  <Text fontSize="xs" color={subtitleColor}>
                    Real-time Data Validation
                  </Text>
                </Flex>
                
                <Flex 
                  align="center" 
                  gap={2}
                  bg={headerBg}
                  backdropFilter="blur(10px)"
                  px={4}
                  py={2}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Box w="8px" h="8px" borderRadius="full" bg="#4fd1c5" />
                  <Text fontSize="xs" color={subtitleColor}>
                    Auto-save Available
                  </Text>
                </Flex>
              </MotionFlex> */}
            </MotionBox>
          </SlideFade>
        </AnimatePresence>
      </Container>

      {/* Scroll to Top Button */}
      <MotionBox
        position="fixed"
        bottom="30px"
        right="30px"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.5 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        zIndex={100}
      >
        <Box
          as="button"
          width="50px"
          height="50px"
          borderRadius="full"
          bgGradient="linear(135deg, #2c7a7a, #4fd1c5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 10px 25px rgba(44, 122, 122, 0.3)"
          transition="all 0.3s ease"
          _hover={{
            transform: 'translateY(-3px)',
            boxShadow: '0 15px 35px rgba(44, 122, 122, 0.4)',
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <Text fontSize="xl" color="white" fontWeight="bold">
            ↑
          </Text>
        </Box>
      </MotionBox>
    </Box>
  );
};

export default UploadSection;